import { useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Alert } from "@/components/alert";
import { Button } from "@/components/button";
import { Icon } from "@/components/icons";
import { Tabs } from "@/components/tabs";
import type { MatchDecision, MatchDecisionStatus } from "@/app/(app)/screening/_data/types";

export const STATUS_LABELS: Record<MatchDecisionStatus, string> = {
  "real-positive": "Real Match",
  "false-positive": "False Match",
  "new-result": "New Match",
};

export const STATUS_ORDER: readonly MatchDecisionStatus[] = [
  "new-result",
  "real-positive",
  "false-positive",
];

export const STATUS_BADGE: Record<MatchDecisionStatus, string> = {
  "new-result": "bg-yellow-100 text-yellow-700",
  "real-positive": "bg-red-100 text-red-600",
  "false-positive": "bg-green-200 text-green-700",
};

export const STATUS_ICON: Record<MatchDecisionStatus, string> = {
  "new-result": "info",
  "real-positive": "cancel",
  "false-positive": "check_circle",
};

export const DEFAULT_DECISION_STATUS: MatchDecisionStatus = "new-result";

type Props = {
  decision?: MatchDecision;
  status: MatchDecisionStatus;
  onSave?: (decision: MatchDecision) => void;
  onCancel?: () => void;
  saving?: boolean;
  error?: string;
  count?: number;
};

export function MatchDecisionPanel({
  decision,
  status,
  count,
  onSave,
  onCancel,
  saving = false,
  error,
}: Props) {
  const bulk = count !== undefined;
  const baseStatus = decision?.status ?? DEFAULT_DECISION_STATUS;
  const baseComment = decision?.comment ?? "";

  const [bulkStatus, setBulkStatus] = useState<MatchDecisionStatus>(DEFAULT_DECISION_STATUS);
  const [comment, setComment] = useState(baseComment);

  const effectiveStatus = bulk ? bulkStatus : status;
  const dirty = bulk || effectiveStatus !== baseStatus || comment !== baseComment;
  const canSave = dirty && !saving;

  function save() {
    if (!canSave) return;
    const next: MatchDecision = {
      status: effectiveStatus,
      comment,
      decidedAt: new Date().toISOString().slice(0, 10),
      decidedBy: "You",
    };
    onSave?.(next);
  }

  function handleKeyDown(e: ReactKeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      save();
    }
  }

  const showLegend = !bulk && Boolean(decision?.comment);

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-tiny font-medium text-grey-900">
            {bulk ? `Status for ${count} selected ${count === 1 ? "result" : "results"}` : "Status"}
          </span>
          {bulk ? (
            <Tabs
              tabs={STATUS_ORDER.map((s) => ({ id: s, label: STATUS_LABELS[s] }))}
              value={bulkStatus}
              onChange={(id) => setBulkStatus(id as MatchDecisionStatus)}
            />
          ) : (
            <StatusBadge status={status} />
          )}
          {showLegend && decision && (
            <p className="text-tiny text-black">&quot;{decision.comment}&quot;</p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="match-decision-comment" className="text-tiny font-medium text-grey-900">
            Comment (optional · ⌘+Enter to save)
          </label>
          <textarea
            id="match-decision-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add context for this decision…"
            rows={3}
            className="resize-none rounded-[4px] border border-grey-200 bg-white px-3 py-2 text-small text-black placeholder:text-grey-500 focus:outline-none focus:ring-2 focus:ring-grey-200"
          />
          {bulk && (
            <p className="text-tiny text-grey-500">
              Left blank, each result keeps its own existing comment. Otherwise, it will be
              applied to all selected results.
            </p>
          )}
        </div>
      </div>

      <div className="mt-8 flex flex-col gap-2">
        {error && (
          <Alert tone="danger" role="alert">
            {error}
          </Alert>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button hierarchy="secondary" size="md" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button hierarchy="primary" size="md" onClick={save} disabled={!canSave}>
            {bulk ? (saving ? "Updating…" : "Update status") : saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: MatchDecisionStatus }) {
  return (
    <span
      className={[
        "inline-flex w-fit items-center gap-1.5 rounded-[4px] px-2 py-1 text-small font-medium",
        STATUS_BADGE[status],
      ].join(" ")}
    >
      <span>{STATUS_LABELS[status]}</span>
      <Icon name={STATUS_ICON[status]} size={20} />
    </span>
  );
}
